import { EnvironmentGroupId } from '@/react/portainer/environments/types';

export type ObservabilityMode = 'deploy-and-connect' | 'connect-only';

export type ObservabilityPreset = 'standard' | 'gke-autopilot' | 'eks-fargate';

export type ObservabilityLogsMode = '' | 'api' | 'daemonset' | 'disabled';

export const LOGS_MODE_OPTIONS: Array<{
  value: ObservabilityLogsMode;
  label: string;
  description: string;
}> = [
  {
    value: '',
    label: 'Preset default',
    description: 'standard → DaemonSet · gke-autopilot / eks-fargate → API',
  },
  {
    value: 'daemonset',
    label: 'DaemonSet',
    description:
      'Mounts /var/log/pods on each node — requires host-level access',
  },
  {
    value: 'api',
    label: 'API',
    description:
      'Kubernetes pod log API — no host access needed, works on Docker Desktop and OrbStack',
  },
  {
    value: 'disabled',
    label: 'Disabled',
    description: 'No log collection',
  },
];

export const OBSERVABILITY_PRESETS = [
  {
    value: 'standard' as const,
    label: 'Standard',
    description: 'Self-managed, EKS on EC2, GKE Standard, AKS',
  },
  {
    value: 'gke-autopilot' as const,
    label: 'GKE Autopilot',
    description: 'GKE Autopilot (no hostPath)',
  },
  {
    value: 'eks-fargate' as const,
    label: 'EKS Fargate',
    description: 'EKS Fargate (no hostPath)',
  },
] as const;

export interface ObservabilityDeployFormValues {
  ingestionKey: string;
  autoProvisionIngestionKey: boolean;
  agentURL: string;
  targetNamespace: string;
  helmReleaseName: string;
  preset: ObservabilityPreset;
  logsMode: ObservabilityLogsMode;
}

export interface ObservabilityDetectionFormValues {
  expectedNamespace: string;
  helmReleaseName: string;
}

export interface ObservabilityK8sFormValues {
  name: string;
  description: string;
  environmentGroupIds: Array<EnvironmentGroupId>;
  oneUptimeURL: string;
  apiKey: string;
  projectID: string;
  mode: ObservabilityMode;
  deploy: ObservabilityDeployFormValues;
  detection: ObservabilityDetectionFormValues;
  tlsSkipVerify: boolean;
  autoRotateKey: boolean;
  // Transient UI state: tracks whether test connection passed for the current credentials.
  // Required to be true when autoRotateKey is enabled before the form can be submitted.
  connectionVerified: boolean;
}

export function buildObservabilityPayload(
  formValues: ObservabilityK8sFormValues
): ObservabilityK8sFormValues {
  return { ...formValues };
}

// Matches the backend's isLoopbackURL check: a loopback OneUptime URL works for
// the Portainer server but is never reachable from agents inside the cluster.
export function isLoopbackURL(rawURL: string): boolean {
  try {
    const { hostname } = new URL(rawURL);
    return (
      hostname === 'localhost' ||
      hostname === '[::1]' ||
      hostname.startsWith('127.')
    );
  } catch {
    return false;
  }
}

export const OBSERVABILITY_K8S_INITIAL_VALUES: ObservabilityK8sFormValues = {
  name: '',
  description: '',
  environmentGroupIds: [],
  oneUptimeURL: 'https://oneuptime.com',
  apiKey: '',
  projectID: '',
  mode: 'connect-only',
  deploy: {
    ingestionKey: '',
    autoProvisionIngestionKey: false,
    agentURL: '',
    targetNamespace: 'oneuptime-agent',
    helmReleaseName: 'kubernetes-agent',
    preset: 'standard' as ObservabilityPreset,
    logsMode: '' as ObservabilityLogsMode,
  },
  detection: {
    expectedNamespace: '',
    helmReleaseName: '',
  },
  tlsSkipVerify: false,
  autoRotateKey: false,
  connectionVerified: false,
};
